import type { ReactNode } from "react";
import { Navigate, Outlet } from "react-router-dom";
import { Lock } from "lucide-react";
import { ButtonLink, EmptyState, Spinner } from "@/components/ui";
import { RequireAuth } from "@/app/guards";
import { canAccess } from "@/app/layouts/navVendor";
import { useMyStore, useSubscription } from "./api";

/** Store dashboard gate: signed-in users without a store go to /sell. */
export function RequireVendor({ children }: { children?: ReactNode }) {
  return (
    <RequireAuth>
      <VendorCheck>{children}</VendorCheck>
    </RequireAuth>
  );
}

function VendorCheck({ children }: { children?: ReactNode }) {
  const store = useMyStore();
  if (store.isLoading)
    return (
      <div className="grid min-h-[60dvh] place-items-center">
        <Spinner className="size-8 text-cyan" label="Loading your store" />
      </div>
    );
  if (!store.data) return <Navigate to="/sell" replace />;
  return <>{children ?? <Outlet />}</>;
}

/**
 * Paywall overlay for creating/publishing when the plan can't publish (PAST_DUE, SUSPENDED, EXPIRED).
 * Content stays visible underneath so vendors can still see their drafts.
 */
export function RequireActiveSubscription({ children }: { children: ReactNode }) {
  const sub = useSubscription();
  if (sub.isLoading || !sub.data || sub.data.entitlements.can_publish) return <>{children}</>;
  return (
    <div className="relative">
      <div aria-hidden className="pointer-events-none select-none opacity-30 blur-[2px]">
        {children}
      </div>
      <div className="absolute inset-0 grid place-items-start justify-center pt-16">
        <EmptyState
          className="rounded-card border border-[var(--border-glow)] bg-elevated/95 shadow-glow-3 backdrop-blur"
          icon={<Lock />}
          title={sub.data.status === "PAST_DUE" ? "Publishing is paused" : "Your plan is inactive"}
          description={
            sub.data.status === "PAST_DUE"
              ? "Your plan has ended. Renew in Billing to publish new listings. Existing ones stay live during the grace period."
              : "Subscribe to publish listings and make your store visible again."
          }
          action={<ButtonLink to="/billing">Go to Billing</ButtonLink>}
        />
      </div>
    </div>
  );
}

/** Hides dashboard areas a staff member's role can't use (the API enforces the same rules). */
export function RequireStoreRole({ path, children }: { path: string; children: ReactNode }) {
  const store = useMyStore();
  if (!store.data) return null;
  if (!canAccess(store.data.role, path))
    return (
      <EmptyState
        icon={<Lock />}
        title="Not available for your role"
        description="Ask the store owner if you need access to this area."
        action={<ButtonLink to="/vendor">Back to overview</ButtonLink>}
      />
    );
  return <>{children}</>;
}
